/** @fileoverview Renders screen content and active sidebar detail screens. */

import { useState } from 'react';
import { ChevronRight, X } from 'lucide-react';

import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent } from '../ui/collapsible';

import type { FabrikComponent, FabrikInputValue, FabrikScreen } from '../../types/apps';
import { useValueContext } from './appRuntimeContext';
import ComponentRenderer from './ComponentRenderer';
import { spacingBefore } from './componentLayout';
import { joinValues } from './helpers';
import { createListFilterStore, ListFilterStoreProvider } from './listFilterStore';
import ScreenChartsProvider from './ScreenChartsProvider';
import type { FieldValues, OpenTarget, ScreenRendererProps } from './types';

type ScreenHeaderProps = {
  screen: FabrikScreen;
  screenIndex: number;
  fields: FieldValues;
  inputs: ScreenRendererProps['navigation']['inputs'];
  isSidePanel?: boolean;
};

/**
 * Shows a detail screen's title and subtitle, or nothing when the screen has no header or both are empty.
 * @param props the screen, its record fields, and whether it sits in the side panel
 */
const ScreenHeader = ({ screen, screenIndex, fields, inputs, isSidePanel }: ScreenHeaderProps) => {
  const { kind, header } = screen;
  const valueContext = { ...useValueContext(), inputs };
  if (kind !== 'detail' || !header) {
    return null;
  }
  const title = joinValues(header.title, fields, ' ', valueContext);
  const subtitle = joinValues(header.subtitle ?? [], fields, ' · ', valueContext);
  if (!title && !subtitle) {
    return null;
  }
  return (
    <header className="mb-5 border-b border-border pb-4">
      {title && (
        <h1
          data-fabrik-selection-path={`/screens/${screenIndex}/header/title`}
          className={`${
            isSidePanel ? 'type-heading' : 'type-title'
          } font-semibold tracking-tight wrap-anywhere text-foreground`}
        >
          {title}
        </h1>
      )}
      {subtitle && (
        <p
          data-fabrik-selection-path={`/screens/${screenIndex}/header/subtitle`}
          className="mt-1 type-small text-muted-foreground"
        >
          {subtitle}
        </p>
      )}
    </header>
  );
};

type ScreenTrailProps = {
  definition: ScreenRendererProps['definition'];
  screenBlocks: ScreenRendererProps['navigation']['screenBlocks'];
  inputs: ScreenRendererProps['navigation']['inputs'];
  onOpenBlock: (blockIndex: number) => void;
};

/**
 * Shows where a nested screen sits, such as "Requests › Yvette Williams": one link per screen
 * opened before it, then the current screen's name. A detail screen is named by its header title.
 * @param props the app definition, the open screens, the inputs and the callback that returns to one
 */
const ScreenTrail = ({ definition, screenBlocks, inputs, onOpenBlock }: ScreenTrailProps) => {
  const valueContext = { ...useValueContext(), inputs };
  const names = screenBlocks.map(({ screenId, fields }) => {
    const screen = definition.screens.find((candidate) => candidate.id === screenId);
    const title =
      screen?.kind === 'detail' && screen.header
        ? joinValues(screen.header.title, fields, ' ', valueContext)
        : '';
    return title || screen?.label || '';
  });
  const lastIndex = names.length - 1;
  return (
    <nav aria-label="Breadcrumb" className="mb-4">
      <ol className="flex flex-wrap items-center gap-1 type-small text-muted-foreground">
        {names.map((name, blockIndex) => (
          <li key={screenBlocks[blockIndex].screenId} className="flex items-center gap-1">
            {blockIndex === lastIndex ? (
              <span aria-current="page" className="font-medium text-foreground">
                {name}
              </span>
            ) : (
              <>
                <button
                  type="button"
                  className="rounded-sm hover:text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                  onClick={() => onOpenBlock(blockIndex)}
                >
                  {name}
                </button>
                <ChevronRight className="size-3.5" aria-hidden="true" />
              </>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

type ScreenComponentsProps = {
  screen: FabrikScreen;
  screenIndex: number;
  fields: FieldValues;
  screenId: string;
  inputs: ScreenRendererProps['navigation']['inputs'];
  commitStandaloneInput: (inputId: string, value: FabrikInputValue | undefined) => void;
  setInputs: ScreenRendererProps['navigation']['setInputs'];
  onOpenTarget: OpenTarget;
  selectedItemId?: string;
  isSidePanel?: boolean;
};

/**
 * Renders a screen's top-level components in order, spaced by role, with buttons that follow each other in one row;
 * configured Grid components own all other grouping and placement. Holds the screen's list filter
 * chips and its charts read, so they reset when the screen goes away.
 * @param props the screen, its record fields, the open-target callback, the selected row, and whether it sits in the side panel
 */
const ScreenComponents = ({
  screen,
  screenIndex,
  fields,
  screenId,
  inputs,
  commitStandaloneInput,
  setInputs,
  onOpenTarget,
  selectedItemId,
  isSidePanel,
}: ScreenComponentsProps) => {
  const { components } = screen;
  const [filterStore] = useState(createListFilterStore);
  /* Buttons that follow each other share one wrapping row; every other component gets its own run. */
  const runs = components.reduce<FabrikComponent[][]>((grouped, component) => {
    const lastRun = grouped[grouped.length - 1];
    if (component.kind === 'button' && lastRun?.[0]?.kind === 'button') lastRun.push(component);
    else grouped.push([component]);
    return grouped;
  }, []);
  const renderComponent = (component: FabrikComponent) => {
    const selectionPath = `/screens/${screenIndex}/components/${components.indexOf(component)}`;
    return (
      <div key={component.id}>
        <ComponentRenderer
          component={component}
          selectionPath={selectionPath}
          screenComponents={components}
          screenId={screenId}
          fields={fields}
          inputs={inputs}
          commitStandaloneInput={commitStandaloneInput}
          setInputs={setInputs}
          selectedItemId={selectedItemId}
          isSidePanel={isSidePanel}
          onOpenTarget={(target, values, sourceItemId) =>
            onOpenTarget(target, values, sourceItemId, screenId)
          }
        />
      </div>
    );
  };

  return (
    <ListFilterStoreProvider value={filterStore}>
      <ScreenChartsProvider
        screenId={screenId}
        components={components}
        fields={fields}
        inputs={inputs}
      >
        <div className="flex min-w-0 flex-col">
          {runs.map((run, runIndex) => {
            const [first] = run;
            const previousRun = runs[runIndex - 1];
            const spacing = spacingBefore(
              previousRun?.[previousRun.length - 1],
              first,
              isSidePanel
            );
            /* A component that renders nothing leaves an empty wrapper; empty:hidden drops its space. */
            return (
              <div
                key={first.id}
                className={`min-w-0 empty:hidden ${spacing} ${
                  run.length > 1 ? 'flex flex-wrap items-start gap-2' : ''
                }`}
              >
                {run.map(renderComponent)}
              </div>
            );
          })}
        </div>
      </ScreenChartsProvider>
    </ListFilterStoreProvider>
  );
};

export default function ScreenRenderer({ definition, navigation }: ScreenRendererProps) {
  const {
    screenBlocks,
    inputs,
    commitStandaloneInput,
    setInputs,
    openTarget,
    goToBlock,
    closeSidebar,
    finishSidebarClose,
  } = navigation;

  return (
    <>
      {screenBlocks.length === 0 && (
        <p className="text-muted-foreground">This app has no screens yet.</p>
      )}
      {screenBlocks.slice(-1).map((block) => {
        const screen = definition.screens.find((candidate) => candidate.id === block.screenId);
        const screenIndex = definition.screens.indexOf(screen!);
        const sidebarScreen = block.sidebar
          ? definition.screens.find((candidate) => candidate.id === block.sidebar?.screenId)
          : undefined;
        if (!screen) return null;

        return (
          <div
            key={screen.id}
            role="region"
            aria-label={screen.label}
            className={`${
              screenBlocks.length > 1
                ? 'animate-in fade-in-0 slide-in-from-right-2 duration-200'
                : ''
            }`}
          >
            <section className="min-w-0">
              {screenBlocks.length > 1 && (
                <ScreenTrail
                  definition={definition}
                  screenBlocks={screenBlocks}
                  inputs={inputs}
                  onOpenBlock={goToBlock}
                />
              )}
              <ScreenHeader
                screen={screen}
                screenIndex={screenIndex}
                fields={block.fields}
                inputs={inputs}
              />
              <ScreenComponents
                screen={screen}
                screenIndex={screenIndex}
                screenId={screen.id}
                fields={block.fields}
                inputs={inputs}
                commitStandaloneInput={commitStandaloneInput}
                setInputs={setInputs}
                selectedItemId={
                  block.sidebar?.open && block.sidebar.sourceScreenId === screen.id
                    ? block.sidebar.sourceItemId
                    : undefined
                }
                onOpenTarget={openTarget}
              />
            </section>
            <Collapsible open={Boolean(sidebarScreen && block.sidebar?.open)} className="min-w-0">
              <CollapsibleContent
                keepMounted
                /* Below lg it takes most of the screen and leaves a strip of the page; from lg it fits its content, from the size step up to half the screen. */
                data-panel-size={sidebarScreen?.size ?? 'md'}
                className="fixed bottom-0 right-0 top-16 z-40 w-4/5 overflow-x-hidden overflow-y-auto lg:w-fit lg:max-w-1/2 lg:min-w-sm lg:data-[panel-size=lg]:min-w-lg 2xl:data-[panel-size=xl]:min-w-2xl border-l border-border bg-card p-5 shadow-xl duration-200 data-open:animate-in data-open:fade-in-0 data-open:slide-in-from-right-2 data-closed:animate-out data-closed:fade-out-0 data-closed:slide-out-to-right-2"
                onAnimationEnd={finishSidebarClose}
              >
                {sidebarScreen && block.sidebar && (
                  <aside aria-label={sidebarScreen.label} className="h-full min-w-0">
                    <div className="mb-3 flex items-center justify-between gap-3 border-b border-border pb-2">
                      <h2 className="truncate type-body font-semibold text-foreground">
                        {sidebarScreen.label}
                      </h2>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        className="text-muted-foreground"
                        aria-label={`Close ${sidebarScreen.label} sidebar`}
                        title={`Close ${sidebarScreen.label} sidebar`}
                        onClick={closeSidebar}
                      >
                        <X aria-hidden="true" />
                      </Button>
                    </div>
                    <ScreenHeader
                      screen={sidebarScreen}
                      screenIndex={definition.screens.indexOf(sidebarScreen)}
                      fields={block.sidebar.fields}
                      inputs={inputs}
                      isSidePanel
                    />
                    {/* Keyed by screen so a new side panel screen remounts its components. */}
                    <ScreenComponents
                      key={sidebarScreen.id}
                      screen={sidebarScreen}
                      screenIndex={definition.screens.indexOf(sidebarScreen)}
                      screenId={sidebarScreen.id}
                      fields={block.sidebar.fields}
                      inputs={inputs}
                      commitStandaloneInput={commitStandaloneInput}
                      setInputs={setInputs}
                      isSidePanel
                      onOpenTarget={openTarget}
                    />
                  </aside>
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>
        );
      })}
    </>
  );
}
